import React from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/proof/PageShell";

const POLICIES = {
    shipping: {
        section: "09",
        eyebrow: "Logistics",
        title: "Shipping",
        crumb: "Shipping",
        body: [
            {
                h: "Origin",
                p: "Every PROOF artifact ships from our atelier in Warsaw, Poland. Orders received before 14:00 CET ship the same business day.",
            },
            {
                h: "Domestic — Poland",
                p: "Free shipping on orders over €200. Below that, a flat €12 charge applies. InPost Paczkomat or DPD courier, 1–2 business days.",
            },
            {
                h: "European Union",
                p: "Free shipping on orders over €300. Below, a flat €18 charge applies. DPD / GLS, 2–5 business days depending on destination.",
            },
            {
                h: "Rest of World",
                p: "Calculated at checkout. DHL Express, 3–7 business days. Local duties and taxes are the recipient's responsibility.",
            },
            {
                h: "Tracking",
                p: "Every order is dispatched with a tracking number, processed via BaseLinker. You'll receive it by email the moment the parcel leaves the studio.",
            },
        ],
    },
    returns: {
        section: "10",
        eyebrow: "Field protocol",
        title: "Returns",
        crumb: "Returns",
        body: [
            {
                h: "Thirty-Day Window",
                p: "If a piece does not earn its place in your wardrobe within thirty days of receipt, return it for a full refund — no questions, no friction.",
            },
            {
                h: "How it works",
                p: "Returns are processed through Wygodne Zwroty. Open your order, generate a label, choose a parcel locker, and drop the piece off. Refunds clear within five business days of arrival at the atelier.",
            },
            {
                h: "Exchanges",
                p: "Size or colour exchanges are free within the EU. Initiate from your Account page.",
            },
            {
                h: "Condition",
                p: "Garments must be returned unworn, with original labels attached. Underwear and final-sale items cannot be returned.",
            },
        ],
    },
    privacy: {
        section: "11",
        eyebrow: "Quiet by design",
        title: "Privacy",
        crumb: "Privacy",
        body: [
            {
                h: "What we collect",
                p: "Only the data required to ship your order and provide customer service: name, email, shipping address, phone (optional), and order history.",
            },
            {
                h: "How we use it",
                p: "To process orders via Shopify and BaseLinker. To send you transactional emails. To respond when you write to us. Nothing else.",
            },
            {
                h: "What we don't do",
                p: "We don't sell or share your data with third parties for marketing. We don't track you across the web. Our newsletter is opt-in only, and one click away from unsubscription.",
            },
            {
                h: "Your rights",
                p: "You may request a copy of your data, ask for corrections, or delete your account at any time. Write to privacy@proof.studio.",
            },
        ],
    },
    terms: {
        section: "12",
        eyebrow: "Fine print",
        title: "Terms",
        crumb: "Terms",
        body: [
            {
                h: "Use of site",
                p: "By using this site you agree to use it in good faith. Don't scrape, don't impersonate, don't break it on purpose.",
            },
            {
                h: "Pricing & availability",
                p: "Prices are listed in EUR including VAT for EU destinations. Stock is limited per Field drop. We reserve the right to cancel orders in case of pricing or stock errors and will refund in full.",
            },
            {
                h: "Intellectual property",
                p: "All imagery, copy and brand marks are owned by PROOF. Editorial use by request only.",
            },
            {
                h: "Governing law",
                p: "Polish law. Disputes are settled by the competent court in Warsaw, unless local consumer law dictates otherwise.",
            },
        ],
    },
    faq: {
        section: "13",
        eyebrow: "Common questions",
        title: "FAQ",
        crumb: "FAQ",
        body: [
            {
                h: "How does sizing run?",
                p: "True to size, with a relaxed shoulder. If you're between sizes, choose the smaller one for a more tailored fit; choose the larger one for a heavier silhouette.",
            },
            {
                h: "Where is PROOF made?",
                p: "Everything is cut, sewn and finished in Warsaw, Poland. No anonymous supply chain.",
            },
            {
                h: "Will pieces restock?",
                p: "Some carryover styles return between Fields. Most drops are one-and-done. Subscribe to the newsletter for restock notice.",
            },
            {
                h: "Do you ship outside the EU?",
                p: "Yes, worldwide via DHL Express. Duties and taxes are payable on arrival.",
            },
            {
                h: "Is there a physical store?",
                p: "By appointment only, in Warsaw. Write to studio@proof.studio for a slot.",
            },
        ],
    },
};

const ALL_SLUGS = Object.keys(POLICIES);

export default function Policy() {
    const { slug } = useParams();
    const policy = POLICIES[slug];

    if (!policy) {
        return (
            <PageShell
                sectionNumber="--"
                eyebrow="Lost in the file"
                title="Not Found"
                crumb="Policy"
                data-testid="page-policy-notfound"
            >
                <p
                    style={{
                        margin: 0,
                        fontFamily: "'EB Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 18,
                        color: "rgba(255,255,255,0.65)",
                        maxWidth: "44ch",
                    }}
                >
                    The policy you're after isn't here. Try a different page.
                </p>
                <div
                    style={{
                        marginTop: 28,
                        display: "flex",
                        gap: 14,
                        flexWrap: "wrap",
                    }}
                >
                    {ALL_SLUGS.map((s) => (
                        <Link
                            key={s}
                            to={`/policy/${s}`}
                            data-testid={`policy-link-${s}`}
                            style={{
                                color: "#fff",
                                textDecoration: "none",
                                border: "1px solid rgba(255,255,255,0.35)",
                                padding: "10px 18px",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 10,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                            }}
                        >
                            {POLICIES[s].title}
                        </Link>
                    ))}
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell
            sectionNumber={policy.section}
            eyebrow={policy.eyebrow}
            title={policy.title}
            crumb={policy.crumb}
            data-testid={`page-policy-${slug}`}
        >
            <div className="proof-grid-policy">
                {/* SIDE INDEX */}
                <nav
                    aria-label="Policy index"
                    style={{
                        position: "sticky",
                        top: 120,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        borderLeft: "1px solid rgba(255,255,255,0.14)",
                        paddingLeft: 18,
                    }}
                >
                    {ALL_SLUGS.map((s) => (
                        <Link
                            key={s}
                            to={`/policy/${s}`}
                            data-testid={`policy-nav-${s}`}
                            style={{
                                color:
                                    s === slug
                                        ? "#fff"
                                        : "rgba(255,255,255,0.5)",
                                textDecoration: "none",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                padding: "10px 0",
                            }}
                        >
                            {POLICIES[s].title}
                        </Link>
                    ))}
                </nav>

                {/* BODY */}
                <article
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "clamp(28px, 3vw, 48px)",
                    }}
                    data-testid="policy-body"
                >
                    {policy.body.map((b, i) => (
                        <section
                            key={i}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 12,
                                paddingBottom: 18,
                                borderBottom: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <h3
                                style={{
                                    margin: 0,
                                    fontFamily:
                                        "'Space Grotesk', Manrope, sans-serif",
                                    fontWeight: 700,
                                    fontSize: "clamp(20px, 1.8vw, 28px)",
                                    letterSpacing: "-0.02em",
                                    textTransform: "uppercase",
                                    color: "#fff",
                                }}
                            >
                                {b.h}
                            </h3>
                            <p
                                style={{
                                    margin: 0,
                                    fontFamily:
                                        "'Space Grotesk', Manrope, sans-serif",
                                    fontSize: 15,
                                    lineHeight: 1.65,
                                    color: "rgba(255,255,255,0.78)",
                                    maxWidth: "62ch",
                                }}
                            >
                                {b.p}
                            </p>
                        </section>
                    ))}
                </article>
            </div>
        </PageShell>
    );
}
